// Display names + descriptions for Data Integrity rule codes (GEN-2367, extended for
// the Applied Validation Rules title/description list). Canonicalizes wording that used
// to be duplicated (and slightly inconsistent) across the four screens that render it:
// PhoneEmailDeepDive, ContactFieldValidation, InvestorFieldValidation, AddressCompletenessSection.
export const RULE_LABELS = {
  EMAIL_FORMAT: 'Email Format Validation',
  PHONE_LENGTH: 'Phone Length Validation',
  INVALID_AREA_CODE: 'Invalid Area Code',
  LINKEDIN_FORMAT: 'LinkedIn URL Format',
  CONTACT_ADDRESS_COMPONENTS: 'Contact Address Components',
  INVESTOR_ADDRESS_COMPONENTS: 'Investor Address Components',
  INVESTOR_PHONE_COMPONENTS: 'Investor Phone Components',
  STATE_CODE: 'State Code',
  COUNTRY_STATE_MISMATCH: 'Country/State Mismatch',
  COUNTRY_ISO: 'Country ISO Validation',
  ZIP_FORMAT: 'ZIP Code Format',
  LAT_LONG_SANITY: 'Lat/Long Sanity Check',
  PRIVATE_RESIDENCE_ADDRESS: 'Private Residence Address Conflict',
  NO_ASSOCIATED_BRANCH: 'No Associated Branch Record',
  DUPLICATE_EMAIL: 'Duplicate Email Detection',
  DUPLICATE_PHONE: 'Duplicate Phone Detection',
  EMAIL_VALIDATION_COVERAGE: 'Email Validation Coverage',
  PHONE_VALIDATION_COVERAGE: 'Phone Validation Coverage',
  EMAIL_VALIDATION_STATUS: 'Email Validation Status',
  PHONE_VALIDATION_STATUS: 'Phone Validation Status',
};

// One-line explanation of what each rule actually flags - used via `ruleDescription()`
// as the CardHeader subheader on screens where a `type` maps 1:1 to a single rule code
// (see AddressCompletenessSection.jsx).
export const RULE_DESCRIPTIONS = {
  EMAIL_FORMAT: "Flags emails with invalid formatting, malformed addresses, disposable domains, or generic role-based inboxes (e.g. info@, admin@).",
  PHONE_LENGTH: 'Flags phone numbers with fewer digits than expected.',
  INVALID_AREA_CODE: 'Flags phone numbers using a known bogus area code (e.g. 000, 555, 123).',
  LINKEDIN_FORMAT: "Flags LinkedIn URLs that don't match the expected personal-profile format (linkedin.com/in/...).",
  CONTACT_ADDRESS_COMPONENTS: 'Flags contact branch addresses missing required components, excluding private residences.',
  INVESTOR_ADDRESS_COMPONENTS: 'Flags investor addresses missing required components (street, city, state, zip), excluding private residences.',
  INVESTOR_PHONE_COMPONENTS: 'Flags investor phone data where the country prefix and phone number are inconsistent or malformed.',
  STATE_CODE: "Flags US records whose state isn't a recognized two-letter state code.",
  COUNTRY_STATE_MISMATCH: 'Flags records with a valid US state code but a country set to somewhere other than the US.',
  COUNTRY_ISO: "Flags records whose country name doesn't map to a valid ISO 3166 country.",
  ZIP_FORMAT: "Flags US ZIP codes that aren't 5 or 9 digits.",
  LAT_LONG_SANITY: 'Flags US branch locations with missing, zero, or geographically implausible coordinates.',
  PRIVATE_RESIDENCE_ADDRESS: 'Flags records marked as a private residence that still have a street address on file.',
  NO_ASSOCIATED_BRANCH: 'Flags active contacts with no associated entry in the branches table (ria_contact_current_branch_data).',
  DUPLICATE_EMAIL: 'Flags email addresses shared by more than one contact record.',
  DUPLICATE_PHONE: 'Flags phone numbers shared by more than one contact record.',
  EMAIL_VALIDATION_COVERAGE: 'Measures how many emails have been sent to the validation API at least once.',
  PHONE_VALIDATION_COVERAGE: 'Measures how many phone numbers have been sent to the validation API at least once.',
  EMAIL_VALIDATION_STATUS: 'Flags emails that are invalid, catch-all, unknown, or were never sent to the validation API.',
  PHONE_VALIDATION_STATUS: 'Flags phone numbers that are invalid or were never sent to the validation API.',
};

const titleize = (code) => code
  .toLowerCase()
  .split('_')
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

export const ruleLabel = (code) => RULE_LABELS[code] || (code ? titleize(code) : '');

export const ruleDescription = (code) => RULE_DESCRIPTIONS[code] || 'Validates this field for data quality issues.';
